import { skipToken } from "@reduxjs/toolkit/query/react";
import { Color } from "@repo/app-types/constants/colors";
import {
  getOutcomeNoun,
  getOutcomePluralNoun,
  isRevenueOutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import {
  asPlaybookId,
  collectPlaybookSections,
  filterPlaybookTreeCriteria,
  getOutcomeDefinitionFromPlaybook,
  type PlaybookCriterionNode,
  type PlaybookId,
  type PlaybookNode,
} from "@repo/app-types/entities/playbook";
import {
  asTeammateId,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import {
  buildCoachingPeriodId,
  calculateMostRecentCoachingPeriodBoundary,
} from "@repo/app-types/entities/teammatePlaybookCoaching";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import { PillDash } from "@repo/ui/Pills/pillDash";
import { PillSkeleton } from "@repo/ui/Pills/pillSkeleton";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { useMemo, useState, type FC } from "react";
import { useParams } from "react-router";
import {
  usePlaybookGlobalAverageScorePerCriteriaQuery,
  usePlaybookTeammateCoachingOpportunitiesQuery,
  usePlaybookTeammateCoachingQuery,
  usePlaybookTeammatePlaybookCriteriaScoresQuery,
  usePlaybookTeammatePlaybookCriterionCoverageSecondsQuery,
  useTeammateHighImpactCriteriaQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { SizedContainer } from "../../components/sizedContainer.js";
import { useAppSelector } from "../../hooks/hook.js";
import { useModalNavigation } from "../../hooks/useModalNavigation.js";
import { selectCompany } from "../company/companySlice.js";
import { buildTeamPerformancePageQueryArgs } from "../filters/buildFilterQueryArgs.js";
import type { FilterDefaults } from "../filters/filters.js";
import { useFilters } from "../filters/filters.js";
import { CriteriaOutcomeCorrelationImpactPill } from "./criteriaOutcomeCorrelationImpactPill.js";
import { CriterionAverageScore } from "./criterionAverageScore.js";
import { CriterionTimeBar } from "./criterionTimeBar.js";
import { GapBarColorScheme, renderGapBarWithTooltip } from "./gapBar.js";
import { GradingPassRatePill } from "./gradingPassRatePill.js";
import { PlaybookCriteriaTreeTable } from "./playbookCriteriaTreeTable.js";
import { usePlaybookCriterionCoverageHelpers } from "./playbookCriterionCoverageHelpers.js";
import {
  PlaybookOpportunityToolbar,
  type PlaybookOpportunityView,
} from "./playbookOpportunityToolbar.js";
import { countTrendDays } from "./trendChart.js";
import { computeTrendDelta, TrendDeltaLabel } from "./trendDeltaLabel.js";

interface TrainingTeammatePagePlaybookProps {
  /** If not provided, will be read from URL params */
  playbookId?: PlaybookId;
  /** If not provided, will be read from URL params */
  teammateId?: TeammateId;
  /** Custom function to generate the criterion navigation path. Defaults to `criteria/${criterionId}` */
  getCriterionPath?: (criterionId: string) => string;
  /**
   * The filter defaults of the page embedding this panel, so it counts the
   * same population. Passed in rather than resolved here because the panel
   * appears on the teammate page, which applies the playbook's defaults, and
   * in an agent's Overview tab, which does not.
   */
  filterDefaults: FilterDefaults;
  /** Shows the current coaching week's opportunity filter and legend. */
  showsCoachingOpportunities?: boolean;
}

/**
 * Renders a playbook assessment table with grading scores for a specific teammate.
 * Can be used as a standalone page (reading from URL params) or embedded in other components (via props).
 *
 * Page accessible via https://<host>/performance/playbooks/<playbookId>/teammates/<teammateId>/playbook
 */
export const TrainingTeammatePagePlaybook: FC<
  TrainingTeammatePagePlaybookProps
> = (props) => {
  const {
    playbookId: playbookIdProp,
    teammateId: teammateIdProp,
    getCriterionPath,
    filterDefaults,
    showsCoachingOpportunities = false,
  } = props;
  const params = useParams();
  const playbookId =
    playbookIdProp ??
    (params.playbookId ? asPlaybookId(params.playbookId) : undefined);

  const teammateId =
    teammateIdProp ??
    (params.teammateId ? asTeammateId(params.teammateId) : undefined);
  if (!playbookId || !teammateId) {
    throw new Error("Missing param playbookId or teammateId.");
  }

  const isCriteriaCoverageEnabled =
    useAppSelector(selectCompany)?.settings.criteria_coverage_enabled ?? false;

  const now = useNow();
  const [opportunityView, setOpportunityView] =
    useState<PlaybookOpportunityView>("opportunities");

  // Use the filter values from the teammate detail page
  const [
    {
      dateRange,
      teammateGroupIds,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      recordingCount,
      dateBasis,
    },
  ] = useFilters(
    [
      "dateRange",
      "teammateGroupIds",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "recordingCount",
      "dateBasis",
    ],
    { playbookId, teammateId },
    filterDefaults,
  );

  const baseArgs =
    teammateGroupIds !== undefined
      ? buildTeamPerformancePageQueryArgs(now, {
          playbookId,
          dateRange,
          teammateGroupIds,
          outcome,
          customFields,
          playbookConditions,
          signalId,
          signalMetadataFields,
          totalRecordingDuration,
          recordingCount,
          dateBasis,
        })
      : undefined;

  const criteriaScoresArgs = baseArgs
    ? {
        playbookId,
        teammateId,
        scope: { ...baseArgs.scope, playbookId },
        filters: baseArgs.filters,
      }
    : skipToken;

  const criterionSlideoverNavigation = useModalNavigation();

  const { data: playbook } = usePlaybookByIdQuery(playbookId);

  // Fetch both outputs for the current period, including when neither exists yet.
  const periodId = buildCoachingPeriodId(
    calculateMostRecentCoachingPeriodBoundary(now),
  );
  const { currentData: coaching, isFetching: isCoachingFetching } =
    usePlaybookTeammateCoachingQuery(
      showsCoachingOpportunities
        ? { playbookId, teammateId, periodId }
        : skipToken,
    );
  const { currentData: opportunities, isFetching: isOpportunitiesFetching } =
    usePlaybookTeammateCoachingOpportunitiesQuery(
      showsCoachingOpportunities
        ? { playbookId, teammateId, periodId }
        : skipToken,
    );
  // The goal alone is not an opportunity list: without a criterion beyond it
  // the table stays unfiltered and unhighlighted.
  const coachingOpportunityCriterionIds = useMemo(() => {
    const goalCriterionId = coaching?.content.criteria[0]?.criterion_id;
    const criterionIds = new Set(
      (opportunities?.content.opportunities ?? []).map(
        ({ criterion_id }) => criterion_id,
      ),
    );
    if (goalCriterionId !== undefined) {
      criterionIds.add(goalCriterionId);
    }
    return criterionIds.size === 1 &&
      goalCriterionId !== undefined &&
      criterionIds.has(goalCriterionId)
      ? new Set<string>()
      : criterionIds;
  }, [coaching, opportunities]);

  const { data: playbookCriteriaScoresData, isLoading: isLoadingScores } =
    usePlaybookTeammatePlaybookCriteriaScoresQuery(criteriaScoresArgs);
  const playbookCriteriaScores = playbookCriteriaScoresData?.criteriaScores;

  // The query is skipped until the group filter loads from preferences, which
  // is still "no scores yet".
  const hasNoScoresYet = criteriaScoresArgs === skipToken || isLoadingScores;

  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  const scoreByNodeId = useMemo(
    () =>
      new Map(
        playbookCriteriaScores?.map((score) => [score.criteria_node_id, score]),
      ),
    [playbookCriteriaScores],
  );

  const { data: playbookCriterionCoverageSeconds } =
    usePlaybookTeammatePlaybookCriterionCoverageSecondsQuery(
      baseArgs
        ? {
            teammateId,
            scope: { ...baseArgs.scope, playbookId },
            filters: baseArgs.filters,
          }
        : skipToken,
    );

  const {
    hasAverageCoverageSeconds,
    getNodeAverageCoverageSeconds,
    getNodeTopPerformerAverageCoverageSeconds,
    maxAverageCoverageSeconds,
  } = usePlaybookCriterionCoverageHelpers(playbookCriterionCoverageSeconds);

  const { data: highImpactCriteria } = useTeammateHighImpactCriteriaQuery({
    playbookId,
    teammateId,
    asOfDateIso: dateRange.end?.toISO() ?? now.endOf("day").toISO(),
  });

  const highImpactDetailByCriteriaId = useMemo(() => {
    if (!highImpactCriteria) return undefined;
    return new Map(
      highImpactCriteria.high_impact_criteria
        .filter((entry) => entry.is_high_impact)
        .map((entry) => [entry.criteria_node_id, entry]),
    );
  }, [highImpactCriteria]);

  // Compare each score with the median for the selected company/group.
  const { data: globalAverageScorePerCriteria } =
    usePlaybookGlobalAverageScorePerCriteriaQuery(
      baseArgs
        ? {
            scope: { ...baseArgs.scope, playbookId },
            filters: baseArgs.filters,
          }
        : skipToken,
    );

  const outcomeNoun = outcomeDefinition
    ? getOutcomeNoun(outcomeDefinition)
    : undefined;
  const isRevenueOutcome = outcomeDefinition
    ? isRevenueOutcomeDefinition(outcomeDefinition)
    : undefined;

  const fullTree = playbook?.assessment_tree;
  // Coaching snapshots criterion ids, so one removed from the playbook since
  // drops out here; the week has opportunities only if any still resolve.
  const opportunityTree = useMemo(
    () =>
      fullTree &&
      filterPlaybookTreeCriteria(fullTree, (criterion) =>
        coachingOpportunityCriterionIds.has(criterion.id),
      ),
    [fullTree, coachingOpportunityCriterionIds],
  );
  const hasCoachingOpportunities =
    opportunityTree !== undefined && opportunityTree.nodes.length > 0;
  const isOpportunitiesView =
    hasCoachingOpportunities && opportunityView === "opportunities";
  const assessmentTree = isOpportunitiesView ? opportunityTree : fullTree;

  // Hold the skeleton until coaching has answered too, so the table does not
  // render in full and then collapse to the week's opportunities.
  const isCoachingLoading =
    (coaching === undefined && isCoachingFetching) ||
    (opportunities === undefined && isOpportunitiesFetching);
  if (!assessmentTree || isCoachingLoading) {
    return (
      <>
        <div className="h-8 w-[70%]">
          <div className="h-full w-full animate-pulse rounded-full bg-gray-300" />
        </div>
      </>
    );
  }

  const renderTimeCell = (node: PlaybookNode) => {
    const teammateAverageSeconds = getNodeAverageCoverageSeconds(node);
    if (teammateAverageSeconds === undefined) return null;
    return (
      <CriterionTimeBar
        teammateAverageSeconds={teammateAverageSeconds}
        topPerformerAverageSeconds={getNodeTopPerformerAverageCoverageSeconds(
          node,
        )}
        maxSeconds={maxAverageCoverageSeconds ?? teammateAverageSeconds}
      />
    );
  };

  const renderNodeScore = (node: PlaybookNode) => {
    const nodeScore = scoreByNodeId.get(node.id);
    const averageScore = nodeScore?.average_score;
    const median = globalAverageScorePerCriteria?.find(
      (score) => score.criteria_node_id === node.id,
    )?.global_median_score;
    const gradingValueType =
      node.type === "criteria" ? node.grading_value_type : "yes_no";

    const isPassRate = gradingValueType === "yes_no";
    const displayScore =
      averageScore === undefined
        ? undefined
        : isPassRate
          ? Math.round(averageScore * 100)
          : parseFloat(averageScore.toFixed(1));
    const displayMedian =
      median == null
        ? undefined
        : isPassRate
          ? Math.round(median * 100)
          : parseFloat(median.toFixed(1));

    if (showsCoachingOpportunities) {
      const isOpportunity =
        node.type === "criteria" &&
        coachingOpportunityCriterionIds.has(node.id);
      const formatScore = (score: number) =>
        isPassRate ? `${score}%` : score.toFixed(1);
      const label =
        displayScore === undefined ? undefined : formatScore(displayScore);

      return (
        <div className="flex items-center gap-3">
          {label !== undefined ? (
            <Pill
              color={isOpportunity ? Color.Blue : Color.Gray}
              size={Size.SMALL}
              label={
                <SizedContainer
                  className="text-center tabular-nums"
                  childrenForSize="100%"
                >
                  {label}
                </SizedContainer>
              }
            />
          ) : hasNoScoresYet ? (
            <PillSkeleton size={Size.SMALL} />
          ) : (
            <PillDash size={Size.SMALL} />
          )}
          <div className="flex w-[50px] shrink-0 items-center">
            {displayScore !== undefined &&
              displayMedian !== undefined &&
              renderGapBarWithTooltip(
                formatScore(displayScore),
                displayScore,
                displayMedian,
                {
                  range: isPassRate ? [0, 100] : [1, 5],
                  colorScheme: isOpportunity
                    ? GapBarColorScheme.Comparison
                    : GapBarColorScheme.Neutral,
                  deltaDecimalPlaces: isPassRate ? 0 : 1,
                  benchmarkLabel: "team median",
                },
              )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3">
        {node.type === "criteria" ? (
          <CriterionAverageScore
            valueType={gradingValueType}
            value={averageScore}
            isLoading={hasNoScoresYet}
            sizedToMax
          />
        ) : (
          <GradingPassRatePill
            size={Size.SMALL}
            passRate={averageScore}
            isLoading={hasNoScoresYet}
            sizedToMax
          />
        )}
        {displayScore !== undefined &&
          displayMedian !== undefined &&
          renderGapBarWithTooltip(
            isPassRate ? `${displayScore}%` : `${displayScore}`,
            displayScore,
            displayMedian,
            { range: isPassRate ? [0, 100] : [1, 5] },
          )}
        {averageScore !== undefined &&
          nodeScore?.average_score_sparkline != null && (
            <TrendDeltaLabel
              delta={computeTrendDelta(nodeScore.average_score_sparkline)}
              gradingValueType={gradingValueType}
              trendDays={countTrendDays(dateRange)}
              size={Size.SMALL}
            />
          )}
      </div>
    );
  };

  const renderCriterionPills = (criterion: PlaybookCriterionNode) => {
    const detail = highImpactDetailByCriteriaId?.get(criterion.id);
    if (!detail || !highImpactCriteria) {
      return null;
    }

    return (
      <CriteriaOutcomeCorrelationImpactPill
        isCollapsed={false}
        color={
          showsCoachingOpportunities
            ? coachingOpportunityCriterionIds.has(criterion.id)
              ? Color.Blue
              : Color.Gray
            : undefined
        }
        rank={detail.rank}
        totalCriteria={highImpactCriteria.total_criteria_count}
        netConversions={Math.round(detail.net_conversions)}
        averageRevenuePerOpp={
          highImpactCriteria.average_revenue_per_opp ?? undefined
        }
        outcomeNoun={outcomeNoun}
        outcomePluralNoun={outcomeDefinition?.plural_noun}
        isRevenueOutcome={isRevenueOutcome}
        isTeammateLevel
      />
    );
  };

  // React Aria only reads defaultExpandedKeys on mount. Expand newly visible
  // ancestors when the view or its opportunity sections change.
  const tableKey = JSON.stringify([
    playbookId,
    teammateId,
    isOpportunitiesView,
    collectPlaybookSections(assessmentTree).map((section) => section.id),
  ]);

  return (
    <div className="space-y-3">
      {hasCoachingOpportunities && (
        <PlaybookOpportunityToolbar
          view={opportunityView}
          onViewChange={setOpportunityView}
          outcomePluralNoun={
            outcomeDefinition
              ? getOutcomePluralNoun(outcomeDefinition)
              : undefined
          }
        />
      )}
      <PlaybookCriteriaTreeTable
        key={tableKey}
        assessmentTree={assessmentTree}
        scoreColumnLabel={
          showsCoachingOpportunities ? "Score vs team" : "Score"
        }
        showsTimeColumn={hasAverageCoverageSeconds && isCriteriaCoverageEnabled}
        renderSection={(section) => ({ score: renderNodeScore(section) })}
        renderCriterion={(criterion) => {
          const defaultCriterionPath = `criteria/${encodeURIComponent(criterion.id)}`;
          return {
            isHighlighted: coachingOpportunityCriterionIds.has(criterion.id),
            titleSuffix: renderCriterionPills(criterion),
            score: renderNodeScore(criterion),
            time: renderTimeCell(criterion),
            onPress: () =>
              criterionSlideoverNavigation.push(
                getCriterionPath?.(criterion.id) ?? defaultCriterionPath,
              ),
          };
        }}
      />
    </div>
  );
};
